import { Badge, Box, Button, Group, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import { OnboardingInvite, OnboardingInviteStatus } from 'src/api/onboardingInvites';
import { validateCompanyName } from 'src/common/utils/companyName';
import { useCreateOnboardingInvite } from './hooks/useCreateOnboardingInvite';

const EMAIL_REGEX =
  /^[^\s@]+@([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

// Mirrors the backend so a mistake is caught before a real invite email is sent:
// FRN_PATTERN in the core-api.
const FRN_REGEX = /^\d{6,7}$/;

const statusBadgeColour: Record<OnboardingInviteStatus, string> = {
  PENDING: 'orange',
  COMPLETED: 'green',
};

const statusLabel: Record<OnboardingInviteStatus, string> = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// The backend identifies the field at fault in the message rather than in a structured
// field, so route by content. A 503 is an outage of the FCA register or Ory, not a problem
// with what was typed, so it stays a notification instead of blaming an input.
const fieldForErrorMessage = (message: string): 'companyName' | 'email' | 'frn' | null => {
  const lowerCaseMessage = message.toLowerCase();
  if (lowerCaseMessage.includes('frn')) return 'frn';
  if (lowerCaseMessage.includes('email')) return 'email';
  if (lowerCaseMessage.includes('company name')) return 'companyName';
  return null;
};

export default function SendToOnboardingPage() {
  const { createInvite, isLoading: isSending } = useCreateOnboardingInvite();

  const form = useForm({
    initialValues: { companyName: '', email: '', frn: '' },
    validate: {
      companyName: validateCompanyName,
      email: value => (EMAIL_REGEX.test(value.trim()) ? null : 'Enter a valid email address'),
      frn: value =>
        FRN_REGEX.test(value.trim())
          ? null
          : 'Enter a valid 6 or 7 digit FCA firm reference number',
    },
  });

  const handleSend = async (values: typeof form.values) => {
    const email = values.email.trim();
    try {
      await createInvite({
        email,
        companyName: values.companyName.trim(),
        frn: values.frn.trim(),
      });
      notifications.show({
        title: 'Invite sent',
        message: `Onboarding invite sent to ${email}.`,
        color: 'green',
      });
      form.reset();
    } catch (error) {
      if (error instanceof ApiError && error.status !== 503) {
        const field = fieldForErrorMessage(error.message);
        if (field) {
          form.setFieldError(field, error.message);
          return;
        }
      }
      const errorMessage =
        error instanceof ApiError ? error.message : 'An unexpected error occurred.';
      notifyAndLogError('Failed to send invite', errorMessage, error);
    }
  };

  const columns: DataTableColumn<OnboardingInvite>[] = [
    { accessor: 'companyName', title: 'Company name' },
    { accessor: 'email', title: 'Email address' },
    { accessor: 'frn', title: 'FCA firm reference number' },
    {
      accessor: 'status',
      title: 'Status',
      render: row => (
        <Badge color={statusBadgeColour[row.status]} variant="light">
          {statusLabel[row.status]}
        </Badge>
      ),
    },
    { accessor: 'createdAt', title: 'Created', render: row => formatDateTime(row.createdAt) },
    { accessor: 'sentAt', title: 'Sent', render: row => formatDateTime(row.sentAt) },
    { accessor: 'expiresAt', title: 'Expires', render: row => formatDateTime(row.expiresAt) },
  ];

  return (
    <Box p="md">
      <Stack gap="lg">
        <Title order={2}>Send to onboarding</Title>

        <form onSubmit={form.onSubmit(handleSend)}>
          <Stack gap="sm">
            <Group align="flex-start" grow>
              <TextInput
                label="Company name"
                placeholder="e.g. Acme Financial Planning Ltd"
                key={form.key('companyName')}
                disabled={isSending}
                {...form.getInputProps('companyName')}
              />
              <TextInput
                label="Email address"
                placeholder="prospect@example.com"
                key={form.key('email')}
                disabled={isSending}
                {...form.getInputProps('email')}
              />
              <TextInput
                label="FCA firm reference number"
                description="6 or 7 digit number"
                placeholder="e.g. 123456"
                inputMode="numeric"
                key={form.key('frn')}
                disabled={isSending}
                {...form.getInputProps('frn')}
              />
            </Group>
            <Group justify="flex-end">
              <Button type="submit" loading={isSending}>
                Send invite
              </Button>
            </Group>
          </Stack>
        </form>

        <SimpleTable<OnboardingInvite>
          resource="admin/onboarding-invites"
          columns={columns}
          title="Invites sent"
          showCount
          enableSearch
          searchAttributes={['companyName', 'email', 'frn']}
          pageSize={20}
        />
      </Stack>
    </Box>
  );
}
